import { SurveyFieldDefinitions, SurveyItemDefinition } from "@aidkitorg/roboscreener/lib/model/core.model";
import { useContext, useEffect, useState } from "react";
import { ConfigurationContext, SupportedLanguage, SurveyContext } from "../Context";
import { InfoDict } from "../Questions/Props";
import { safeParse, safeParseValidatedFormula } from "../Util";
import { RichText } from "@aidkitorg/types/lib/survey";
import { LANG_DICTS } from "../Localization";
import { ApplicationStateContextType } from "../Contexts/ApplicationStateContext";

type ApplicationStateChangedFn = ApplicationStateContextType["applicationStateChanged"];
export class RoboScreener {
  deployment: string;
  applicant?: string;
  applicationStateChanged?: ApplicationStateChangedFn

  // Proto-RS mechanics
  collectedDependencies: boolean = false;
  allDeps: Record<string, Record<string, true>> = {};
  formulas: Record<string, string>;
  conditionOns: Record<string, string>;

  // All current info
  info: InfoDict = {};

  constructor(
    deployment: string, 
    formulas: Record<string, string>, 
    conditionOns?: Record<string, string>, 
    applicationStateChanged?: ApplicationStateChangedFn
  ) {
    this.deployment = deployment;
    this.formulas = formulas;
    this.conditionOns = conditionOns || {};
    this.applicationStateChanged = applicationStateChanged;
  }

  setKey(key: string, value: string) {
    this.info = {...this.info, [key]: value};
    // Update _created_at timestamp to "now" when a field is edited locally
    // This enables instant "Last Modified" conditionals in the UI
    // Note: This is an approximation - the actual DB timestamp may differ slightly after save
    const createdAtKey = key + '_created_at';
    this.info[createdAtKey] = new Date().toISOString();
    
    // Recompute dependent fields
    this.computeUpdates(key);
    this.computeUpdates(createdAtKey);

    this.applicationStateChanged?.({
      type: "update-info",
      update: this.info
    });
  }

  /** Batch set multiple keys at once, only computing updates once at the end */
  setKeys(updates: Record<string, string>) {
    const keys = Object.keys(updates);
    if (keys.length === 0) return;

    const now = new Date().toISOString();
    const allDirtyKeys: string[] = [];

    // Set all values and their _created_at timestamps without computing updates
    for (const key of keys) {
      this.info[key] = updates[key];
      const createdAtKey = key + '_created_at';
      this.info[createdAtKey] = now;
      allDirtyKeys.push(key, createdAtKey);
    }

    // Compute updates once with all dirty keys together
    this.computeUpdatesInternal(allDirtyKeys);
  }

  /**
   * Internal method to compute updates for one or more dirty keys.
   * Uses a Set to track what's currently in the dirty queue to avoid duplicates,
   * but allows keys to be re-added if their value changes during cascade.
   */
  private computeUpdatesInternal(dirtyKeys: string[]) {
    let dirty = [...dirtyKeys];
    // Track what's currently in the dirty queue to avoid duplicate entries
    const inDirtyQueue = new Set(dirtyKeys);

    const extractDeps = (key: string, deps: Set<string>) => {
      Array.from(deps).map((dep) => {
        if (dep === key) return;
        if (!this.allDeps[dep]) {
          this.allDeps[dep] = {}
        }
        this.allDeps[dep][key] = true;
      })
    }

    const compute = (key: string, force?: boolean) => {
      let doCompute = true;
      if (this.conditionOns[key]) {
        const [deps, result, error] = this.computeWithDeps(this.info, this.conditionOns[key]);
        extractDeps(key, deps);
        const parsedResult = safeParse(result);

        doCompute = parsedResult === true;
        if (!doCompute && !force) {
          return;
        }
      }

      const infoToComputeWith = doCompute ? this.info : { ...this.info };
      const [deps, result, error] = this.computeWithDeps(infoToComputeWith, this.formulas[key]);
      extractDeps(key, deps);

      if (doCompute) {
        if (result !== this.info[key]) {
          this.info[key] = result;

          // Create _created_at for computed fields, same as setKey(s) does for edits
          // Allowing conditionals gated on a computed field's last modified date can fire in-session.
          const createdAtKey = key + '_created_at';
          this.info[createdAtKey] = new Date().toISOString();

          // Only add to dirty if not already queued
          if (!inDirtyQueue.has(key)) {
            dirty.push(key);
            inDirtyQueue.add(key);
          }
          if(!inDirtyQueue.has(createdAtKey)){
            dirty.push(createdAtKey);
            inDirtyQueue.add(createdAtKey);
          }
        }

        if ((error || this.info[key + "_error"]) && error !== this.info[key + "_error"]) {
          this.info[key + "_error"] = error;
        }
      }
    }

    // If we haven't yet computed dependencies, run all formulas with the proxy to compute deps
    if (!this.collectedDependencies) {
      Object.keys(this.formulas).map((key) => compute(key, true));
      this.collectedDependencies = true;
    }

    // Process dirty keys - allow re-processing if value changed (key will be re-added to queue)
    while (dirty.length > 0) {
      const next = dirty.pop()!;
      inDirtyQueue.delete(next);  // Remove from tracking so it can be re-added if needed

      const toRecompute = this.allDeps[next] || {};
      Object.keys(toRecompute).forEach((key) => compute(key));
    }
  }

  computeWithDeps(info: Record<string, any>, formula: string): [Set<string>, string, string] {
    const deps = new Set<string>();
    const proxy = new Proxy(info, {
      get: (target, key: string) => {
        deps.add(key);
        return info[key]
      }
    })
    try {
      const func = Function(
        "return (function(info, org, screener) { const out = " + formula + "; return out });"
      )();
      let result = func(proxy);
      if (result !== undefined && result !== null) {
        result = result.toString();
      } else {
        result = '';
      }
      return [deps, result, ''];
    } catch (e) {
      return [deps, '', e instanceof Error ? e.message : (e as string)];
    }
  }

  computeUpdates(dirtyKey?: string) {
    const dirtyKeys = dirtyKey ? [dirtyKey] : [];
    this.computeUpdatesInternal(dirtyKeys);
  }
}

/** Always use RS */
export function useRoboScreener(particularMethod?: string) {
  return true;
}

/** This hook converts the python survey API response to the RS infoDefs style for use in Computed and Validated */
type SurveyItemDefinitionWithContent = SurveyItemDefinition & { index: number, content: RichText };
type SurveyFieldsWithContent = Record<string, SurveyItemDefinitionWithContent>;

type LegacyFieldDef = SurveyItemDefinition & { index: number };
type LegacyFieldDefs = Record<string, LegacyFieldDef>;

export function useSurveyDescription<GetContent extends boolean = false>(props?: { getContent?: GetContent }) {
  const survey = useContext(SurveyContext);

  type Description = GetContent extends true ? SurveyFieldsWithContent : LegacyFieldDefs;

  const [infoDefs, setInfoDefs] = useState({} as Description);

  useEffect(() => {
    if ((survey.sections || []).length === 0) return;
    
    const defs = {} as Description;
    let index = 0;
    for (const s of survey.sections) {
      for (const q of s.Questions) {
        const key = q["Target Field"];
        if (!key) continue; // Don't need these

        const type = q['Field Type'];
        const NOT_NEEDED = ['Flag Review', 'Show Field', 'Show Date'];
        if (NOT_NEEDED.indexOf(type) !== -1) continue;
        
        const def = {
          index,
          key,
          type,
          ...(props?.getContent ? { content: Object.keys(q).reduce((content: RichText, k: string) => {
            let supportedKey = k;
            if (k === 'English Content') supportedKey = "en";
            if (k === "Spanish Content") supportedKey = "es";
            if (LANG_DICTS[supportedKey as SupportedLanguage]) {
              content[supportedKey as SupportedLanguage] = q[k];
            }
            return content;
          }, {} as RichText) } : {}),
          editableBy: q['Who Can Edit'],
          name: q['Question'],
          metadata: q['Metadata'],
          options: q['Additional Options'],
          choices: q['Options (if relevant)']
        } as GetContent extends true ? SurveyItemDefinitionWithContent : LegacyFieldDef;
        index++;

        // Add formula
        if (type === 'Computed') {
          def['formula'] = safeParse(q['Metadata'] || '{}')['formula'];
        } else if (type === 'Validated') {
          def['formula'] = safeParseValidatedFormula(q['Metadata'] || '');
        }

        // Add conditional (only if computed or validated)
        if (['Computed','Validated'].indexOf(q['type']) !== -1) {
          if (q['Conditional On']) {
            const cKey = q['Conditional On'];
            if (q['Conditional On Value']) {
              def['conditional'] = q['Conditional On Value'].map((cv: string) => {
                return `info.${cKey} === '${cv}'`;
              }).join(' || ');
            } else {
              def['conditional'] = `info.${cKey} !== '' && info.${cKey} !== undefined`;
            }
          } else if (q['Metadata'] && type !== 'Validated') {
            const metadata = safeParse(q['Metadata']);
            if (metadata?.conditional) {
              def['conditional'] = metadata.conditional;
            }
          }
        }
        

        // Now check if this infoDef exists, exactly like the one we have
        if (!infoDefs[key]) {
          defs[key] = def;
          continue;
        }

        /** 
         * 
         for (const k in def) {
           const prop = k as keyof SurveyItemDefinition;
           if (infoDefs[key][prop] !== def[prop]) {
             defs[key] = def;
             break;
           }
         }
         */
      }
    }

    if (Object.keys(defs).length !== 0) {
      setInfoDefs((prevState: Description) => {
        return Object.assign({}, {...prevState, ...defs})
      });
    }

  }, [survey.sections]);

  return infoDefs;
}
